// hooks/useInactivityTimeout.tsx - 12시간 비활성 자동 로그아웃

import { useEffect, useRef, useCallback, useState } from 'react';
import { useClientSnapshot } from '@/hooks/useClientSnapshot';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-hot-toast';
import { getInactivityConfig } from '@/lib/auth/security-config';

const MOBILE_USER_AGENT = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
const isMobileUserAgent = () => MOBILE_USER_AGENT.test(navigator.userAgent.toLowerCase());

interface UseInactivityTimeoutOptions {
  timeoutHours?: number; // 비활성 타임아웃 (시간)
  warningMinutes?: number; // 경고 표시 시간 (분)
  enabled?: boolean; // 기능 활성화 여부
}

export function useInactivityTimeout({
  timeoutHours,
  warningMinutes,
  enabled = true
}: UseInactivityTimeoutOptions = {}) {
  // 기본값을 설정에서 가져오기
  const defaultConfig = getInactivityConfig();
  const baseTimeoutHours = timeoutHours ?? defaultConfig.TIMEOUT_HOURS;
  const baseWarningMinutes = warningMinutes ?? defaultConfig.WARNING_MINUTES;
  const { logout } = useAuth();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  // null until the first activity timer starts (= inactive for 0s)
  const lastActivityRef = useRef<number | null>(null);
  const warningShownRef = useRef<boolean>(false);
  const isMobileRef = useRef<boolean>(false);
  // Rendered copies of the refs above (the refs drive the timers)
  const [isWarningShown, setIsWarningShown] = useState(false);
  const isMobile = useClientSnapshot(isMobileUserAgent, false);

  // 개발 환경에서 테스트용 더 짧은 타임아웃
  const effectiveTimeoutHours = process.env.NODE_ENV === 'development' 
    ? Math.min(baseTimeoutHours, 0.1) // 개발환경에서는 최대 6분
    : baseTimeoutHours;

  // 개발 환경에서 테스트용 더 짧은 경고 시간
  const effectiveWarningMinutes = process.env.NODE_ENV === 'development'
    ? Math.min(baseWarningMinutes, 1) // 개발환경에서는 최대 1분
    : baseWarningMinutes;

  // 모바일 환경 감지
  useEffect(() => {
    const checkMobile = () => {
      isMobileRef.current = isMobileUserAgent();
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 사용자 활동 감지 함수
  // Named so the warning toast's buttons can restart the timers.
  const resetTimer = useCallback(function resetTimer() {
    const now = Date.now();
    lastActivityRef.current = now;
    
    // 경고가 표시되었다면 숨기기
    if (warningShownRef.current) {
      warningShownRef.current = false;
      setIsWarningShown(false);
      toast.dismiss(); // 경고 토스트 제거
    }

    // 기존 타이머들 제거
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
    }

    // 새로운 타이머 설정
    const timeoutMs = effectiveTimeoutHours * 60 * 60 * 1000; // 시간을 밀리초로 변환
    const warningMs = timeoutMs - (effectiveWarningMinutes * 60 * 1000);

    // 경고 타이머
    warningRef.current = setTimeout(() => {
      warningShownRef.current = true;
      setIsWarningShown(true);
      
      const warningMessage = isMobileRef.current 
        ? `Session will expire soon. Auto logout in ${effectiveWarningMinutes} minutes.`
        : `For security, you will be logged out in ${effectiveWarningMinutes} minutes. Click anywhere to continue.`;

      toast.custom((t) => {
        return (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-lg max-w-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                                 <h3 className="text-sm font-medium text-yellow-800">
                   Session Expiring Soon
                 </h3>
                <div className="mt-1 text-sm text-yellow-700">
                  <p>{warningMessage}</p>
                </div>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => {
                    resetTimer();
                    toast.dismiss(t.id);
                  }}
                  className="text-yellow-800 hover:text-yellow-900"
                >
                                     <span className="sr-only">Close</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            {!isMobileRef.current && (
              <div className="mt-3 flex space-x-2">
                <button
                  onClick={() => {
                    resetTimer();
                    toast.dismiss(t.id);
                  }}
                  className="flex-1 bg-yellow-600 text-white text-sm font-medium py-2 px-3 rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                                     Continue
                 </button>
                 <button
                   onClick={() => toast.dismiss(t.id)}
                   className="flex-1 bg-gray-200 text-gray-800 text-sm font-medium py-2 px-3 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                 >
                   Ignore
                </button>
              </div>
            )}
          </div>
        );
      }, {
        duration: effectiveWarningMinutes * 60 * 1000, // 경고 시간만큼 표시
        position: isMobileRef.current ? 'top-center' : 'top-right',
      });
    }, warningMs);

    // 로그아웃 타이머
    timeoutRef.current = setTimeout(() => {
      const logoutMessage = isMobileRef.current 
        ? 'You have been automatically logged out due to inactivity.'
        : 'You have been automatically logged out due to inactivity. Your cart has been preserved.';
        
      toast.error(logoutMessage, {
        duration: 8000,
        position: isMobileRef.current ? 'top-center' : 'top-right',
      });
      logout();
    }, timeoutMs);
  }, [effectiveTimeoutHours, effectiveWarningMinutes, logout]);

  // 사용자 활동 이벤트 리스너
  useEffect(() => {
    if (!enabled) return;

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'touchmove',
      'click',
      'focus',
      'input',
      'change'
    ];

    const handleActivity = () => {
      resetTimer();
    };

    // 이벤트 리스너 등록
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // 페이지 가시성 변경 감지 (모바일에서 중요)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // 페이지가 다시 보이면 타이머 리셋
        resetTimer();
      }
    };

    // 페이지 포커스 감지 (모바일에서 중요)
    const handleFocus = () => {
      resetTimer();
    };

    // 페이지 블러 감지
    const handleBlur = () => {
      // 페이지가 블러되어도 타이머는 계속 실행
      // 모바일에서 앱 전환 시에도 세션 유지
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // 모바일 특별 이벤트
    if (isMobileRef.current) {
      // 터치 이벤트 추가
      document.addEventListener('touchstart', handleActivity, true);
      document.addEventListener('touchmove', handleActivity, true);
      document.addEventListener('touchend', handleActivity, true);
      
      // 모바일 브라우저 특별 이벤트
      window.addEventListener('orientationchange', handleActivity);
      window.addEventListener('resize', handleActivity);
    }

    // 초기 타이머 시작
    resetTimer();

    // 클린업
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      
      if (isMobileRef.current) {
        document.removeEventListener('touchstart', handleActivity, true);
        document.removeEventListener('touchmove', handleActivity, true);
        document.removeEventListener('touchend', handleActivity, true);
        window.removeEventListener('orientationchange', handleActivity);
        window.removeEventListener('resize', handleActivity);
      }
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningRef.current) {
        clearTimeout(warningRef.current);
      }
    };
  }, [enabled, resetTimer]);

  // 현재 비활성 시간 반환 (디버깅용)
  const getInactiveTime = useCallback(() => {
    const now = Date.now();
    const inactiveSeconds = Math.floor((now - (lastActivityRef.current ?? now)) / 1000);
    const hours = Math.floor(inactiveSeconds / 3600);
    const minutes = Math.floor((inactiveSeconds % 3600) / 60);
    const seconds = inactiveSeconds % 60;
    
    return {
      totalSeconds: inactiveSeconds,
      formatted: `${hours}h ${minutes}m ${seconds}s`
    };
  }, []);

  return {
    resetTimer,
    getInactiveTime,
    isWarningShown,
    isMobile,
    timeoutHours: effectiveTimeoutHours,
    warningMinutes: effectiveWarningMinutes
  };
} 